import {
  MatrixBlockType,
  TempLessonBlock,
} from '@/models/_deprecated/LessonBlock';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { zeroPad } from 'react-countdown';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { dropHandler } from '@/utils/dragdrop';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import { DragIcon } from '@/components/icons';
import { LESSON_BLOCKS_LIMIT as maxLength } from '@/constants';
import { useAutoFocus } from '@/data/hook/useAutoFocus';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempLesson } from '@/models/Lesson';
import { bodyText, card, secondaryInput } from '@/styles/styles';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import alert from '@/utils/shared/alert';
import { useRef, useState } from 'react';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import { CourseStudioProps } from '../info/CourseStudioInfo.hooks';
import { getPreviousBlocksLessons } from './CourseStudioLessons.hooks';
import { DirectLessonMatrix } from './DirectLessonsMatrix';
import { shouldDisplayDirectLessons } from '@/utils/courseStudio/matrix';

type DraggableItemProps = {
  lesson: TempLesson;
  blockIndex: number;
  index: number;
  removeLesson: () => void;
  studioPermissions: StudioPermissions;
  globalLessonIndex: number;
  setLessonElementRef: (index: number) => (el: HTMLInputElement | null) => void;
};

export function DraggableItem(props: DraggableItemProps) {
  const {
    lesson,
    index,
    removeLesson,
    studioPermissions,
    blockIndex,
    globalLessonIndex,
    setLessonElementRef,
  } = props;

  const { control } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { allowOtherSteps } = studioPermissions;

  const allowSimpleEditing =
    allowOtherSteps.simpleEditing ||
    allowOtherSteps.createEditing ||
    allowOtherSteps.advancedEditing;

  const allowAdvancedEditing =
    allowOtherSteps.advancedEditing || allowOtherSteps.createEditing;

  const disabled = !allowSimpleEditing && !allowAdvancedEditing;

  return (
    <Draggable
      draggableId={String(lesson.tempId || lesson.id)}
      index={index}
      isDragDisabled={!allowAdvancedEditing}
    >
      {provided => (
        <li ref={provided.innerRef} {...provided.draggableProps}>
          <div className="flex items-center gap-2 justify-between w-full">
            <div {...provided.dragHandleProps}>
              <ConditionalWrapper
                condition={!allowAdvancedEditing}
                wrapper={
                  <Tooltip
                    text={t('notAllowedLimitedEditing')}
                    classNameContainer="flex w-full"
                  />
                }
              >
                <DragIcon className="w-4 h-4" />
              </ConditionalWrapper>
            </div>
            <fieldset
              className={twMerge(
                secondaryInput,
                'flex p-0 w-full items-center px-2.5',
                'transition-all duration-200 ease-in-out',
              )}
            >
              <label className="text-sm text-nowrap">
                {tCommon('lesson', {
                  order: zeroPad(globalLessonIndex + 1, 2),
                })}{' '}
                -
              </label>
              <Controller
                control={control}
                name={`lessonBlocks.${blockIndex}.lessons.${index}.name`}
                render={({ field: { onChange, value = '' } }) => (
                  <ConditionalWrapper
                    condition={disabled}
                    wrapper={
                      <Tooltip
                        text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                        classNameContainer="flex w-full"
                      />
                    }
                  >
                    <SecondaryInput
                      ref={setLessonElementRef(index)}
                      value={value}
                      onChange={onChange}
                      disabled={disabled}
                      className={{
                        base: 'border-none p-0 items-center',
                        button: disabled ? 'hidden' : '',
                      }}
                      required
                      error={!value}
                      clearWrapper={
                        <ConditionalWrapper
                          condition={
                            !studioPermissions.allowOtherSteps.advancedEditing
                          }
                          wrapper={
                            <Tooltip
                              text={t('notAllowedLimitedEditing')}
                              classNameContainer="flex"
                            />
                          }
                        >
                          <IconButton
                            disabled={
                              !studioPermissions.allowOtherSteps.advancedEditing
                            }
                            type="button"
                            icon={
                              <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                            }
                            onClick={removeLesson}
                          />
                        </ConditionalWrapper>
                      }
                    />
                  </ConditionalWrapper>
                )}
              />
            </fieldset>
          </div>
        </li>
      )}
    </Draggable>
  );
}

type DraggableColumnProps = CourseStudioProps & {
  block: TempLessonBlock;
  index: number;
  studioPermissions: StudioPermissions;
  removeBlock: () => void;
  blocks?: TempLessonBlock[];
  setBlockElementRef: (index: number) => (el: HTMLInputElement | null) => void;
};

function DraggableColumn(props: DraggableColumnProps) {
  const {
    block,
    index,
    studioPermissions,
    removeBlock,
    blocks = [],
    setBlockElementRef,
  } = props;

  const { control, watch } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const {
    fields: lessons,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `lessonBlocks.${index}.lessons`,
    keyName: 'tempId',
    rules: {
      maxLength,
    },
  });

  const { setElementRef: setLessonElementRef } = useAutoFocus(lessons.length);

  const hasMaxLessons = () => {
    const lessonsQuantity = watch('coursePath.lessonsQuantity');

    const lessonsCount = blocks.reduce(
      (acc, block) => acc + block.lessons.length,
      0,
    );

    if (!lessonsQuantity) return false;

    return lessonsCount >= lessonsQuantity;
  };

  const handleAddLesson = () => {
    if (hasMaxLessons()) {
      return alert.error(t('maxLessonsReached'));
    }
    const newLesson = new TempLesson();
    append(newLesson);
  };

  const handleRemoveLesson = (index: number) => {
    remove(index);
  };

  const { allowOtherSteps } = studioPermissions;

  const allowSimpleEditing =
    allowOtherSteps.simpleEditing ||
    allowOtherSteps.createEditing ||
    allowOtherSteps.advancedEditing;

  const allowAdvancedEditing =
    allowOtherSteps.advancedEditing || allowOtherSteps.createEditing;

  const disabled = !allowSimpleEditing && !allowAdvancedEditing;

  const [isRemovingBlock, setIsRemovingBlock] = useState(false);

  function handleRemoveBlock() {
    removeBlock();
    setIsRemovingBlock(false);
  }

  function handleVerifyLessonsIntoBlock() {
    if (lessons.length) {
      setIsRemovingBlock(true);
    } else {
      handleRemoveBlock();
    }
  }

  return (
    <Draggable
      draggableId={String(block.tempId || block.id)}
      index={index}
      isDragDisabled={!allowAdvancedEditing}
    >
      {provided => (
        <li
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="flex grow"
        >
          <div {...provided.dragHandleProps} className="pt-2.5">
            <ConditionalWrapper
              condition={!allowAdvancedEditing}
              wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
            >
              <DragIcon data-testid="dragIcon" />
            </ConditionalWrapper>
          </div>

          <div className={twJoin(card, 'flex-1 flex flex-col gap-2.5 py-2.5')}>
            <div className="flex justify-between items-center">
              <fieldset className="flex-1 flex items-center">
                <label
                  className={twMerge(
                    bodyText,
                    '!text-primary font-500 text-nowrap',
                  )}
                >
                  {t('block', { index: zeroPad(index + 1, 2) })} -
                </label>

                <Controller
                  control={control}
                  name={`lessonBlocks.${index}.name`}
                  render={({ field: { onChange, value = '' } }) => (
                    <ConditionalWrapper
                      condition={disabled}
                      wrapper={
                        <Tooltip
                          text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                          classNameContainer="flex w-full"
                        />
                      }
                    >
                      <SecondaryInput
                        ref={setBlockElementRef(index)}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        className={{
                          base: 'border-none p-0 relative',
                          button: 'absolute right-2.5 top-1',
                          input:
                            'w-[94%] grow-0 flex text-primary font-500 text-[1rem]',
                        }}
                        required
                        error={!value}
                        clearWrapper={
                          <ConditionalWrapper
                            condition={
                              !studioPermissions.allowOtherSteps.advancedEditing
                            }
                            wrapper={
                              <Tooltip
                                text={t('notAllowedLimitedEditing')}
                                classNameContainer="flex"
                              />
                            }
                          >
                            <IconButton
                              disabled={
                                !studioPermissions.allowOtherSteps
                                  .advancedEditing
                              }
                              type="button"
                              icon={
                                <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                              }
                              onClick={handleVerifyLessonsIntoBlock}
                            />
                          </ConditionalWrapper>
                        }
                      />
                    </ConditionalWrapper>
                  )}
                />
              </fieldset>
            </div>

            <Droppable
              droppableId={String(block.tempId || block.id)}
              type={MatrixBlockType.LESSON}
              isDropDisabled={!allowAdvancedEditing}
            >
              {provided => (
                <ul
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={twJoin(
                    'flex flex-col gap-2.5 min-h-[2rem]',
                    'transition-all duration-200 ease-in-out',
                  )}
                >
                  <ConditionalRenderer
                    condition={lessons.length > 0}
                    fallback={
                      <p className={twMerge(bodyText, 'text-base-content/50')}>
                        {t('dragOrAddLesson')}
                      </p>
                    }
                  >
                    {lessons.map((lesson, lessonIndex) => {
                      const previousBlocksLessons = getPreviousBlocksLessons(
                        index,
                        blocks,
                      );

                      return (
                        <DraggableItem
                          key={String(lesson.tempId || lesson.id)}
                          lesson={lesson}
                          blockIndex={index}
                          index={lessonIndex}
                          removeLesson={() => handleRemoveLesson(lessonIndex)}
                          studioPermissions={studioPermissions}
                          globalLessonIndex={
                            previousBlocksLessons + lessonIndex
                          }
                          setLessonElementRef={setLessonElementRef}
                        />
                      );
                    })}
                  </ConditionalRenderer>
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
            <div className="mt-auto">
              <ConditionalWrapper
                condition={disabled}
                wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
              >
                <MainButton
                  disabled={!allowAdvancedEditing}
                  color="custom"
                  className="text-primary font-500 capitalize"
                  icon={<PlusIcon />}
                  text={t('addLesson')}
                  onClick={handleAddLesson}
                />
              </ConditionalWrapper>
            </div>
          </div>
          <ModalDisable
            visible={isRemovingBlock}
            translationString="deleteLessonBlock"
            onClickConfirm={handleRemoveBlock}
            modalType="delete"
            onClickCancel={() => setIsRemovingBlock(false)}
          />
        </li>
      )}
    </Draggable>
  );
}

type Props = CourseStudioProps & {
  slug?: string;
  studioPermissions: StudioPermissions;
  disabled?: boolean;
};

export default function LessonBlocksMatrixStep(props: Props) {
  const { studioPermissions } = props;

  const { control, watch, setValue } = useFormContext<CourseStudioFormFields>();

  const {
    fields: blocks,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'lessonBlocks',
    keyName: 'tempId',
    rules: {
      maxLength,
    },
  });

  const directLessons = watch('directLessons');

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { setElementRef: setBlockElementRef } = useAutoFocus(blocks.length);

  const hasMaxBlocks = () => {
    return blocks.length >= maxLength;
  };

  const handleAddBlock = () => {
    if (hasMaxBlocks()) {
      return alert.error(t('maxBlocksReached'));
    }

    const newBlock = new TempLessonBlock();

    append(newBlock);
  };

  function findColumnIndexById(columns: TempLessonBlock[], id: string) {
    return columns.findIndex(col => String(col.tempId || col.id) === id);
  }

  function handleDragBlock(result: DropResult) {
    const { source, destination } = result;

    if (!destination) return;

    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    move(sourceIndex, destinationIndex);
  }

  function handleSameColumn(result: DropResult) {
    const { source, destination } = result;

    if (!destination) return;

    const columnIndex = findColumnIndexById(blocks, destination.droppableId);

    if (columnIndex === -1) return;

    const currentLessons = watch(`lessonBlocks.${columnIndex}.lessons`) || [];

    const [movedLesson] = currentLessons.splice(source.index, 1);
    currentLessons.splice(destination.index, 0, movedLesson);

    setValue(`lessonBlocks.${columnIndex}.lessons`, currentLessons);
  }

  function handleDifferentColumn(result: DropResult) {
    const { source, destination } = result;

    if (!destination) return;

    const sourceColumnIndex = findColumnIndexById(blocks, source.droppableId);
    const destinationColumnIndex = findColumnIndexById(
      blocks,
      destination.droppableId,
    );

    if (sourceColumnIndex === -1 || destinationColumnIndex === -1) return;

    const sourceLessons =
      watch(`lessonBlocks.${sourceColumnIndex}.lessons`) || [];
    const destinationLessons =
      watch(`lessonBlocks.${destinationColumnIndex}.lessons`) || [];

    const [movedLesson] = sourceLessons.splice(source.index, 1);
    destinationLessons.splice(destination.index, 0, movedLesson);

    setValue(`lessonBlocks.${sourceColumnIndex}.lessons`, sourceLessons);
    setValue(
      `lessonBlocks.${destinationColumnIndex}.lessons`,
      destinationLessons,
    );
  }

  function handleDragLessonOnlyToBlock(result: DropResult) {
    const { source, destination } = result;

    if (!destination) return;

    const destinationColumnIndex = findColumnIndexById(
      blocks,
      destination.droppableId,
    );

    const sourceLessons = watch('directLessons') || [];

    const destinationLessons =
      watch(`lessonBlocks.${destinationColumnIndex}.lessons`) || [];

    const [movedLesson] = sourceLessons.splice(source.index, 1);
    destinationLessons.splice(destination.index, 0, movedLesson);

    setValue(`directLessons`, sourceLessons);
    setValue(
      `lessonBlocks.${destinationColumnIndex}.lessons`,
      destinationLessons,
    );
  }

  function handleSelfDragLessonOnly(result: DropResult) {
    const lessons = watch('directLessons') || [];

    const dndResult = dropHandler(result, lessons);

    setValue('directLessons', dndResult);
  }

  const handleDragLessonOnly = (result: DropResult) => {
    const { source, destination } = result;

    if (source.droppableId === destination?.droppableId) {
      handleSelfDragLessonOnly(result);
      return;
    }

    if (source.droppableId !== destination?.droppableId) {
      handleDragLessonOnlyToBlock(result);
      return;
    }
  };

  function handleDragLesson(result: DropResult) {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === 'lessonsOnly') {
      handleDragLessonOnly(result);
      return;
    }
    if (source.droppableId === destination.droppableId) {
      handleSameColumn(result);
      return;
    }

    if (source.droppableId !== destination.droppableId) {
      handleDifferentColumn(result);
      return;
    }
  }

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;

    if (!destination) return;

    const sameIndex = source.index === destination.index;
    const sameId = source.droppableId === destination.droppableId;

    if (sameIndex && sameId) {
      return;
    }

    if (type === MatrixBlockType.BLOCK) {
      handleDragBlock(result);
    }

    if (type === MatrixBlockType.LESSON) {
      handleDragLesson(result);
    }
  };

  const disabled =
    !studioPermissions.allowOtherSteps.createEditing &&
    !studioPermissions.allowOtherSteps.advancedEditing;

  const onAddLesson = useRef<() => void>(() => {});

  return (
    <article className="flex flex-col gap-2.5">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable
          droppableId="blocks"
          type={MatrixBlockType.BLOCK}
          isDropDisabled={disabled}
        >
          {provided => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={twJoin(
                'flex flex-col gap-2.5 max-h-[calc(100vh-22rem)]',
                scrollBarYClassName,
              )}
            >
              <ConditionalRenderer
                condition={shouldDisplayDirectLessons(directLessons, blocks)}
              >
                <DirectLessonMatrix
                  studioPermissions={studioPermissions}
                  onAddLesson={onAddLesson}
                />
              </ConditionalRenderer>
              {blocks.map((block, index) => (
                <DraggableColumn
                  key={String(block.tempId || block.id)}
                  block={block}
                  index={index}
                  removeBlock={() => remove(index)}
                  blocks={watch('lessonBlocks')}
                  setBlockElementRef={setBlockElementRef}
                  {...props}
                />
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      <div className="mt-auto shrink-0 flex gap-2.5">
        <ConditionalRenderer
          condition={
            !!directLessons && directLessons.length > 0 && blocks.length === 0
          }
        >
          <ConditionalWrapper
            condition={!studioPermissions.allowOtherSteps.advancedEditing}
            wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
          >
            <MainButton
              disabled={!studioPermissions.allowOtherSteps.advancedEditing}
              color="custom"
              className="text-primary font-500 capitalize gap-1"
              icon={<PlusIcon />}
              text={t('addLesson')}
              onClick={onAddLesson.current}
            />
          </ConditionalWrapper>
        </ConditionalRenderer>
        <ConditionalWrapper
          condition={!studioPermissions.allowOtherSteps.advancedEditing}
          wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
        >
          <MainButton
            disabled={disabled}
            color="custom"
            className="text-primary font-500 capitalize gap-1"
            icon={<PlusIcon />}
            text={t('addBlock')}
            onClick={handleAddBlock}
          />
        </ConditionalWrapper>
      </div>
    </article>
  );
}
